import React, { useState, useEffect } from 'react';
import { Button, Select } from '../../common';
import { Modal } from '../../common/Modal';
import { Document } from '../../../gql/graphql';
import { useAdminTranslation } from '../../../modules/i18n';

interface DocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingDocument: Document | null;
  onSave: (document: Partial<Document>) => void;
}

const DocumentModal: React.FC<DocumentModalProps> = ({ isOpen, onClose, editingDocument, onSave }) => {
  const { t } = useAdminTranslation();
  const [formData, setFormData] = useState<Partial<Document>>({
    title: '',
    folderId: '',
    extension: '',
    fileSize: 0
  });

  // Update form data when editingDocument changes
  useEffect(() => {
    if (editingDocument) {
      setFormData({
        title: editingDocument.title,
        folderId: editingDocument.folderId,
        extension: editingDocument.extension,
        fileSize: editingDocument.fileSize
      });
    } else {
      setFormData({
        title: '',
        folderId: '',
        extension: '',
        fileSize: 0
      });
    }
  }, [editingDocument]);

  // Get folder options for parent selection
  const folderOptions = [
    { value: '', label: t('content.documents.modal.document.rootFolder') }
    // Note: We would need to get folders from a separate query
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const documentData: Partial<Document> = {
      ...formData
    };

    onSave(documentData);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('content.documents.modal.document.title')}
      size='md'
      height='auto'
      footer={
        <div className='flex justify-end space-x-3'>
          <Button type='button' variant='ghost' onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type='submit' variant='primary' onClick={handleSubmit}>
            {t('content.documents.modal.document.updateButton')}
          </Button>
        </div>
      }>
      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>{t('content.documents.modal.document.nameLabel')}</label>
          <input
            type='text'
            value={formData.title || ''}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className='w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-gray-900 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary dark:border-border-dark dark:bg-dark-background-tertiary dark:text-dark-text'
            placeholder={t('content.documents.modal.document.namePlaceholder')}
            required
          />
        </div>

        <div>
          <label className='mb-1 block text-sm font-medium text-gray-700 dark:text-dark-text-secondary'>
            {t('content.documents.modal.document.folderLabel')}
          </label>
          <Select value={formData.folderId || ''} onChange={(value) => setFormData({ ...formData, folderId: value || '' })} options={folderOptions} />
        </div>
      </form>
    </Modal>
  );
};

export default DocumentModal;
